import { MatchMode } from "@/graphql/generated/operations";
import { dataToDesign, matchToData } from "@/components/match/match-design-mapping";
import { DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS, EMPTY_MATCH_DATA } from "@/components/match/match-design-rules";
import type { MatchData } from "@/components/match/match-design-types";
import { parseRequirements } from "@/pages/project/project-plan";
import { requirementsToMatchData } from "@/pages/project/requirements-to-match";
import { companyRequirementsData, listingSearchTarget } from "./listing-search-target";

const KEYS = ["conditions", "companyConditions", "teamProfile", "candidateAssessmentDetail",
  "candidateAssessmentStrictness", "candidateAssessmentOutcomeFocus", "requirementLeniency", "powerScoreLens", "missingPowerScore"] as const;

/** Only the setup owned by the listing; the match's brief, documents and other settings stay intact. */
export function listingRequirementsDesign(data: MatchData): Record<string, unknown> {
  const design = dataToDesign(data);
  return { ...Object.fromEntries(KEYS.map(key => [key, design[key]])), aiEnabled: data.aiEnabled };
}

export function listingRequirementsData(raw: Record<string, unknown>): MatchData {
  const company = listingSearchTarget(raw) === "COMPANIES";
  const requirements = parseRequirements(raw.requirements);
  const legacy = company ? companyRequirementsData(raw.companyRequirements)
    : { ...EMPTY_MATCH_DATA, ...requirementsToMatchData(requirements, "RANK") };
  if (!company && legacy.teamProfile) {
    legacy.teamProfile = { ...legacy.teamProfile, elementWeights: {
      ...Object.fromEntries(Object.keys(DEFAULT_TEAM_PROFILE_ELEMENT_WEIGHTS).map(key => [key, 0])),
      ...requirements.elementWeights,
    } };
  }
  const stored = raw.matchRequirements;
  const design = stored && typeof stored === "object" ? stored as Record<string, unknown> : listingRequirementsDesign(legacy);
  const data = matchToData({ design, name: null, description: null,
    matchMode: company ? MatchMode.RankCompanies : MatchMode.Rank });
  // An empty criteria set cannot encode the global switch through per-criterion aiMode.
  return { ...data, aiEnabled: "aiEnabled" in design && typeof design.aiEnabled === "boolean" ? design.aiEnabled : data.aiEnabled };
}

export function hasListingRequirements(data: MatchData): boolean {
  return data.matchMode === MatchMode.RankCompanies
    ? !!data.companyConditions?.some(row => row.active)
    : data.conditions.some(row => row.active)
      || Object.values(data.teamProfile?.elementWeights ?? {}).some(weight => Number.isFinite(weight) && weight > 0);
}

export function sameListingRequirements(left: MatchData, right: MatchData): boolean {
  return listingRequirementDifferences(left, {
    searchTarget: right.matchMode === MatchMode.RankCompanies ? "COMPANIES" : "PEOPLE",
    matchRequirements: listingRequirementsDesign(right),
  }).length === 0;
}

export interface ListingRequirementDifference {
  kind: "condition" | "power" | "setting";
  key: string;
  before: unknown;
  after: unknown;
}

function stable(value: unknown): string {
  return JSON.stringify(value, (_key, entry: unknown) => entry && typeof entry === "object" && !Array.isArray(entry)
    ? Object.fromEntries(Object.entries(entry).sort(([a], [b]) => a.localeCompare(b))) : entry) ?? "";
}

/** The status and its explanation share the same comparison, including legacy ownership rules. */
export function listingRequirementDifferences(snapshot: MatchData, raw: Record<string, unknown>): ListingRequirementDifference[] {
  const current = listingRequirementsData(raw);
  const canonical = !!raw.matchRequirements && typeof raw.matchRequirements === "object";
  const company = listingSearchTarget(raw) === "COMPANIES";
  const before = listingRequirementsDesign(snapshot);
  const after = listingRequirementsDesign(canonical ? current : { ...snapshot,
    ...(company ? { companyConditions: current.companyConditions } : { conditions: current.conditions }),
  });
  const differences: ListingRequirementDifference[] = [];
  const rowKey = company ? "companyConditions" : "conditions";
  function rows(design: Record<string, unknown>): Record<string, unknown>[] {
    return ((design[rowKey] as Record<string, unknown>[] | null) ?? [])
      .filter(row => canonical || company || (row.active && row.__typename === "ExpertiseCondition"))
      .map(row => {
        if (!canonical && !company) return { __typename: row.__typename, expertise: row.expertise,
          level: row.level, requirement: row.requirement ?? "OPTIONAL", operator: row.operator };
        // Compositions count people in team matches; listing rankings evaluate one subject at a time.
        const { composition: _composition, ...values } = row;
        return values;
      });
  }
  const oldRows = rows(before), newRows = rows(after);
  // Remove equal rows before pairing changes, so sorting or duplicate criteria cannot invent edits.
  for (let i = oldRows.length - 1; i >= 0; i--) {
    const equal = newRows.findIndex(row => stable(row) === stable(oldRows[i]));
    if (equal >= 0) { oldRows.splice(i, 1); newRows.splice(equal, 1); }
  }
  for (const row of oldRows) {
    const identity = (value: Record<string, unknown>) => stable([value.__typename, value.expertise ?? value.id ?? value.industryId ?? value.languageTag ?? value.value]);
    let index = newRows.findIndex(next => identity(next) === identity(row));
    if (index < 0) index = newRows.findIndex(next => next.__typename === row.__typename);
    differences.push({ kind: "condition", key: rowKey, before: row, after: index < 0 ? undefined : newRows.splice(index, 1)[0] });
  }
  for (const row of newRows) differences.push({ kind: "condition", key: rowKey, before: undefined, after: row });
  if (!company) {
    const oldWeights = snapshot.teamProfile?.elementWeights ?? {};
    const newWeights = canonical ? current.teamProfile?.elementWeights ?? {} : parseRequirements(raw.requirements).elementWeights;
    const keys = canonical ? new Set([...Object.keys(oldWeights), ...Object.keys(newWeights)]) : Object.keys(newWeights);
    for (const key of keys) if (oldWeights[key] !== newWeights[key]) {
      differences.push({ kind: "power", key, before: oldWeights[key], after: newWeights[key] });
    }
  }
  if (canonical) {
    for (const key of KEYS) {
      if (key === "conditions" || key === "companyConditions" || key === "teamProfile") continue;
      if (stable(before[key]) !== stable(after[key])) differences.push({ kind: "setting", key, before: before[key], after: after[key] });
    }
  }
  return differences;
}

export function listingRequirementsMatchSnapshot(snapshot: MatchData, raw: Record<string, unknown>): boolean {
  return listingRequirementDifferences(snapshot, raw).length === 0;
}
